/**
 * Timeline Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A sophisticated animated timeline component for displaying chronological content.
 * Features alternating layout design with smooth carousel navigation and responsive behavior.
 * 
 * CORE FEATURES:
 * - Animated timeline with alternating top/bottom item layout
 * - Smooth carousel navigation with react-slick integration
 * - Chronological content display with year-based organization
 * - Responsive design with center mode and padding adjustments
 * - Interactive navigation with arrow controls and drag support
 * - Configurable autoplay with pause-on-hover functionality
 * 
 * VISUAL DESIGN:
 * - Alternating timeline items (even: bottom, odd: top)
 * - Professional timeline icons with year indicators
 * - Connecting lines between timeline elements
 * - Centered carousel layout with customizable padding
 * - Smooth linear animations for continuous scrolling
 * - Responsive content cards with flexible sizing
 * 
 * INTERACTION FEATURES:
 * - Touch/swipe support for mobile devices
 * - Draggable slider functionality
 * - Hover pause behavior for user interaction
 * - Arrow navigation for precise control
 * - Infinite loop scrolling for continuous browsing
 * 
 * DATA STRUCTURE:
 * - JSON-based timeline data with year and content arrays
 * - Flexible content list support for each timeline item
 * - Dynamic rendering based on data structure
 * - Conference-specific data integration capability
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React-slick carousel with professional settings
 * - State management for timeline data synchronization
 * - Ref-based slider control for programmatic navigation
 * - CSS-based alternating layout with conditional rendering
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of timeline item objects (currently overridden with local data)
 * @param {number} props.data[].year - Year for timeline item
 * @param {Array} props.data[].content - Array of content strings for timeline item
 * @returns {JSX.Element} Animated timeline component with carousel navigation
 */
import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Timeline.css';
import timelineData from '../../data/timeline.json';

/**
 * Timeline Component
 * 
 * Professional animated timeline with alternating layout and carousel navigation.
 * Displays chronological content with smooth scrolling and interactive controls.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Timeline data array (overridden with local data)
 * @returns {JSX.Element} Timeline interface with carousel navigation
 */
function Timeline({data}) {
  // For the conference, we will use the data from the json file
  data = timelineData
  const [timelineItems, setTimelineItems] = useState([]);
  const sliderRef = useRef(null);

  useEffect(() => {
    if (data) {
      setTimelineItems(data);
    }
  }, [data]);

  const settings = {
    dots: false,
    infinite: true,
    speed: 6000,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    cssEase: 'linear',
    arrows: true,
    pauseOnHover: true,
    swipe: true,
    draggable: true,
    centerMode: true,
    centerPadding: '100px'
   
  };

  return (
    <div className="timeline-navigation">
      <div className="timeline-container">
        <Slider ref={sliderRef} {...settings}>
          {timelineItems.map((item, index) => (
            <div className={`timeline-item ${index % 2 === 0 ? 'timeline-item-bottom' : ''}`} key={index}>
              {index % 2 === 0 && (
                <>
                  <div className="timeline-icon">{item.year}</div>
                  <div className="timeline-line"></div>
                  <div className={`timeline-content timeline-content-${index + 1}`}>
                    <ul>
                      {item.content.map((content, idx) => (
                        <li key={idx}>{content}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
             
              <div className="timeline-icon-wrapper">
                {index % 2 === 1 && (
                  <>
                    <div className={`timeline-content timeline-content-${index + 1}`}>
                      <ul>
                        {item.content.map((content, idx) => (
                          <li key={idx}>{content}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="timeline-line"></div>    
                    <div className="timeline-icon">{item.year}</div> 
                  </>
                )}
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
}

export default Timeline;
